import { FormikProvider } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/Checkbox';
import { Chip } from 'src/components/Chip';
import { CircularProgress } from 'src/components/CircularProgress';
import { Dialog } from 'src/components/Dialog';
import { Grid } from 'src/components/Grid';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { CartItem } from 'src/models/cart/_types';
import { PaymentOption } from 'src/models/cart/_types';
import { getPaymentOptionDetails } from 'src/models/cart/utils';
import { StripePlan } from 'src/models/payment/_types';
import { isStripeConnected } from 'src/models/platform/utils';
import { ClientSearchResult } from 'src/models/user/_types';
import { getUserDisplayName } from 'src/models/user/utils';
import { ClientAutocomplete } from 'src/pages/Clients/WaiverPage/ClientAutocomplete';
import { AddCoupon } from 'src/shared/AddCoupon';
import { CouponForm, CouponFormFields } from 'src/shared/Forms/CouponForm';
import { FormAutocomplete } from 'src/shared/Forms/Fields/FormAutocomplete';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { PaymentPlanSelect } from 'src/shared/Forms/Selects/PaymentPlanSelect';
import { FormTrainerSelect } from 'src/shared/Forms/Selects/TrainerSelect';
import { ItemType, getItemTypeLabel } from 'src/shared/constants/itemTypes';
import { formatFullDate } from 'src/utils/dateFormatter';

import { useItemSettings } from './useItemSettings';

interface ItemSettingsProps {
  item: CartItem;
  onSubmit: (updatedItem: CartItem) => void;
  editing?: boolean;
  itemType?: ItemType;
  user?: any; // User for the item settings - if not provided, user-dependent features are hidden
}

const Field = ({ label, value, editing = true, children }) => {
  const realValue = value;

  const valueIsElem = React.isValidElement(value);
  const valueIsText =
    typeof realValue === 'string' || typeof realValue === 'number';
  const valueIsArray = Array.isArray(realValue);
  const valueIsDate = realValue instanceof Date;
  const valueIsObj =
    !valueIsElem &&
    typeof realValue === 'object' &&
    realValue !== null &&
    !valueIsArray &&
    !valueIsDate;
  const keys = valueIsObj && Object.keys(realValue);

  const valueIsCoupon = valueIsObj && value.code && value.id;

  if (editing) {
    return children;
  }
  if (!value) {
    return null;
  }
  if (valueIsArray && value.length === 0) {
    return null;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0 }}>
          {valueIsElem && <>{realValue}</>}
          {valueIsText && <>{realValue}</>}
          {valueIsArray && (
            <Stack direction="row" spacing={0}>
              {realValue.map((v, i) => (
                <Chip
                  key={
                    typeof v === 'object' && v !== null
                      ? v.id || i
                      : v + ' ' + i
                  }
                  label={
                    typeof v === 'object' && v !== null && 'name' in v
                      ? v.name
                      : v
                  }
                  sx={{ mr: 2 }}
                />
              ))}
            </Stack>
          )}
          {valueIsDate && <>{formatFullDate(realValue)}</>}
          {valueIsCoupon && (
            <Box>
              {value.code}
              <Typography color="success" variant="body2">
                {value.description}
              </Typography>
            </Box>
          )}
          {valueIsObj && !valueIsCoupon && (
            <>
              {(keys || []).map((key) => (
                <React.Fragment key={key}>
                  <Typography
                    component="span"
                    color="text.secondary"
                    variant="body2"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {key}:&nbsp;
                  </Typography>
                  <Typography component="span" variant="body2" sx={{ mr: 2 }}>
                    {realValue[key]}
                  </Typography>
                </React.Fragment>
              ))}
            </>
          )}
        </Stack>
      </Typography>
    </Box>
  );
};

export const ItemSettings: React.FC<ItemSettingsProps> = ({
  item,
  onSubmit,
  itemType,
  editing,
  user: propUser,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'clients.paymentDialog',
  });
  const { session } = useSession();
  const {
    formik,
    trainers,
    showCreateCouponForm,
    setShowCreateCouponForm,
    couponType,
    setCouponType,
    handleCreateCoupon,
    isSubscription,
    yearly,
    weekly,
    user,
    currentUser,
  } = useItemSettings(item, onSubmit, {
    user: propUser,
  });

  return (
    <>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2}>
            <Field
              label={t('itemType')}
              value={item.itemType ? getItemTypeLabel(item.itemType) : ''}
              editing={editing}
            >
              {' '}
            </Field>
            {isSubscription(formik.values.paymentOption) ? (
              <>
                <Field
                  label={t('paymentPlan')}
                  value={getPaymentOptionDetails(formik.values.paymentOption)}
                  editing={editing}
                >
                  <PaymentPlanSelect
                    label={t('selectPaymentPlan')}
                    userId={currentUser.id}
                    value={formik.values.paymentOption?.id?.toString()}
                    oneOffSubscriptionId={
                      user && item.itemId
                        ? `${item.itemId}-${user.id}-${item.itemType?.includes('Package') ? 'package' : 'item'}`
                        : '0'
                    }
                    onCreatePlan={(plan: Partial<StripePlan>) => {
                      const paymentOption = {
                        id: plan.id,
                        name: plan.name,
                        type: 'subscription',
                        interval: plan.interval,
                        intervalCount: plan.intervalCount,
                        amount: plan.amount,
                        currency: plan.currency,
                        custom: true, // Mark newly created plans as custom
                      } as PaymentOption;

                      formik.setFieldValue('paymentOption', paymentOption);
                      formik.setFieldValue('coupon', null);
                      formik.setFieldValue('billingCycleAnchor', null);
                      formik.setFieldValue('overrideProrationBehavior', null);
                      formik.setFieldValue('backdateIsStartdate', false);
                    }}
                    onChange={(value) => {
                      if (Array.isArray(value)) return;

                      const paymentOption = {
                        id: value.id,
                        name: value.name,
                        type: 'subscription',
                        interval: value.interval,
                        intervalCount: value.intervalCount,
                        amount: value.amount,
                        currency: value.currency,
                      } as PaymentOption;

                      formik.setFieldValue('paymentOption', paymentOption);
                      formik.setFieldValue('coupon', null);
                      formik.setFieldValue('billingCycleAnchor', null);
                      formik.setFieldValue('overrideProrationBehavior', null);
                      formik.setFieldValue('backdateIsStartdate', false);
                    }}
                    hideCreateButton={!isStripeConnected(session)}
                  />
                </Field>
                {user && (
                  <Field
                    label={t('applyCoupon')}
                    value={formik.values.coupon}
                    editing={editing}
                  >
                    <AddCoupon
                      couponType="subscription"
                      text={t('applyCoupon')}
                      user={user}
                      modelId={item.itemId}
                      modelType={item.itemType}
                      selectedCoupon={formik.values.coupon}
                      onApplyCoupon={(coupon) => {
                        formik.setFieldValue('coupon', coupon);
                      }}
                      onRemoveCoupon={() => {
                        formik.setFieldValue('coupon', null);
                      }}
                      openCouponModal={() => {
                        setCouponType('subscription');
                        setShowCreateCouponForm(true);
                      }}
                    />
                  </Field>
                )}

                <Field
                  label={t('subscriptionStartDate')}
                  value={formik.values.startOn || new Date()}
                  editing={editing}
                >
                  <FormDatePicker
                    label={t('subscriptionStartDate')}
                    name="startOn"
                    helperText={t('subscriptionStartDateHelper')}
                    onChange={(date) => {
                      formik.setFieldValue('startOn', date);
                      formik.setFieldValue('backdateIsStartdate', false);
                    }}
                  />
                </Field>
                {formik.values.startOn &&
                  new Date(formik.values.startOn).setHours(0, 0, 0, 0) <
                    new Date().setHours(0, 0, 0, 0) && (
                    <Field
                      label={t('billingCycleAnchorBackdate')}
                      value={formik.values.backdateIsStartdate}
                      editing={editing}
                    >
                      <Checkbox
                        value={!!formik.values.backdateIsStartdate}
                        checked={!!formik.values.backdateIsStartdate}
                        onClick={() => {
                          formik.setFieldValue(
                            'backdateIsStartdate',
                            !formik.values.backdateIsStartdate
                          );
                        }}
                        label={
                          <>
                            {t('setBillingCycleAnchorBackdate')}
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {t('backdateExplanation')}
                            </Typography>
                          </>
                        }
                      />
                    </Field>
                  )}

                {/* Hide the Grid that wraps Fields when its not relevant so the grid doesnt take space even though all the fields inside are hidden */}
                {(editing ||
                  formik.values.billingCycleAnchor ||
                  formik.values.overrideProrationBehavior) && (
                  <>
                    {/* anchor month */}
                    {yearly(formik.values.paymentOption)
                      ? !formik.values.backdateIsStartdate && (
                          <Grid container spacing={2}>
                            <Grid
                              size={{
                                xs: 12,
                                md: formik.values.billingCycleAnchor ? 6 : 12,
                              }}
                            >
                              <Field
                                label={t('billingCycleAnchor')}
                                value={formik.values.billingCycleAnchor}
                                editing={editing}
                              >
                                <FormAutocomplete
                                  label={t('billingCycleAnchor')}
                                  name="billingCycleAnchor"
                                  options={[
                                    { label: 'January', value: 1 },
                                    { label: 'February', value: 2 },
                                    { label: 'March', value: 3 },
                                    { label: 'April', value: 4 },
                                    { label: 'May', value: 5 },
                                    { label: 'June', value: 6 },
                                    { label: 'July', value: 7 },
                                    { label: 'August', value: 8 },
                                    { label: 'September', value: 9 },
                                    { label: 'October', value: 10 },
                                    { label: 'November', value: 11 },
                                    { label: 'December', value: 12 },
                                  ]}
                                />
                              </Field>
                            </Grid>
                            {formik.values.billingCycleAnchor && (
                              <Field
                                label={t('prorationBehavior')}
                                value={formik.values.overrideProrationBehavior}
                                editing={editing}
                              >
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <FormAutocomplete
                                    label={t('prorationBehavior')}
                                    name="overrideProrationBehavior"
                                    getOptionLabel={(option) =>
                                      option?.label || ''
                                    }
                                    options={[
                                      {
                                        label: t('prorateFirstCharge'),
                                        value: 'prorate',
                                      },
                                      {
                                        label: t('chargeFullAmount'),
                                        value: 'no_prorate',
                                      },
                                      {
                                        label: t('doNotCharge'),
                                        value: 'no_charge',
                                      },
                                    ]}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {t('chargeBehaviorDescription')}
                                  </Typography>
                                </Grid>
                              </Field>
                            )}
                          </Grid>
                        )
                      : !formik.values.backdateIsStartdate && (
                          <Grid container spacing={2}>
                            <Grid
                              size={{
                                xs: 12,
                                md: formik.values.billingCycleAnchor ? 6 : 12,
                              }}
                            >
                              <Field
                                label={t('billingCycleAnchor')}
                                value={formik.values.billingCycleAnchor?.label}
                                editing={editing}
                              >
                                <FormAutocomplete
                                  label={t('billingCycleAnchor')}
                                  name="billingCycleAnchor"
                                  options={
                                    weekly(formik.values.paymentOption)
                                      ? [
                                          {
                                            label: 'Sunday',
                                            value: 'sunday',
                                          },
                                          {
                                            label: 'Monday',
                                            value: 'monday',
                                          },
                                          {
                                            label: 'Tuesday',
                                            value: 'tuesday',
                                          },
                                          {
                                            label: 'Wednesday',
                                            value: 'wednesday',
                                          },
                                          {
                                            label: 'Thursday',
                                            value: 'thursday',
                                          },
                                          {
                                            label: 'Friday',
                                            value: 'friday',
                                          },
                                          {
                                            label: 'Saturday',
                                            value: 'saturday',
                                          },
                                        ]
                                      : [
                                          { label: '1st', value: 1 },
                                          { label: '2nd', value: 2 },
                                          { label: '3rd', value: 3 },
                                          ...Array.from(
                                            { length: 25 },
                                            (_, i) => ({
                                              label: `${i + 4}th`,
                                              value: i + 4,
                                            })
                                          ),
                                          {
                                            label: 'End of month',
                                            value: -1,
                                          },
                                        ]
                                  }
                                />
                              </Field>
                            </Grid>
                            {formik.values.billingCycleAnchor && (
                              <Grid size={{ xs: 12, md: 6 }}>
                                <Field
                                  label={t('prorationBehavior')}
                                  value={
                                    formik.values.overrideProrationBehavior
                                      ?.label
                                  }
                                  editing={editing}
                                >
                                  <FormAutocomplete
                                    label={t('prorationBehavior')}
                                    name="overrideProrationBehavior"
                                    getOptionLabel={(option) =>
                                      option?.label || ''
                                    }
                                    options={[
                                      {
                                        label: t('prorateFirstCharge'),
                                        value: 'prorate',
                                      },
                                      {
                                        label: t('chargeFullAmount'),
                                        value: 'no_prorate',
                                      },
                                      {
                                        label: t('doNotCharge'),
                                        value: 'no_charge',
                                      },
                                    ]}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {t('chargeBehaviorDescription')}
                                  </Typography>
                                </Field>
                              </Grid>
                            )}
                          </Grid>
                        )}
                  </>
                )}

                <Field
                  label={t('subscriptionCancelDate')}
                  value={formik.values.cancelDate}
                  editing={editing}
                >
                  <FormDatePicker
                    label={t('subscriptionCancelDate')}
                    name="cancelDate"
                    minDate={new Date(Date.now() + 86400000)} // Add 24 hours in milliseconds
                    helperText={t('subscriptionCancelDateHelper')}
                  />
                </Field>
              </>
            ) : (
              <>
                <Field
                  label={t('itemPrice')}
                  value={
                    typeof formik.values.paymentOption?.amount === 'number'
                      ? formik.values.paymentOption?.amount / 100
                      : undefined
                  }
                  editing={editing}
                >
                  <FormTextField
                    label={t('itemPrice')}
                    name="paymentOption.amount"
                    type="number"
                    value={
                      typeof formik.values.paymentOption?.amount === 'number'
                        ? formik.values.paymentOption?.amount / 100
                        : undefined
                    }
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value)) {
                        formik.setFieldValue(
                          'paymentOption.amount',
                          Math.round(value * 100)
                        );
                        // Mark as custom pricing when user manually changes the amount
                        formik.setFieldValue('paymentOption.custom', true);
                      } else {
                        formik.setFieldValue('paymentOption.amount', undefined);
                      }
                    }}
                  />
                </Field>
                {itemType?.supportsQuantity && (
                  <Field
                    label={t('quantity')}
                    value={formik.values.quantity}
                    editing={editing}
                  >
                    <FormTextField
                      label={t('quantity')}
                      name="quantity"
                      type="number"
                      value={formik.values.quantity}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value) && value > 0) {
                          formik.setFieldValue('quantity', value);
                        }
                      }}
                    />
                  </Field>
                )}
                {user && (
                  <Field
                    label={t('applyCoupon')}
                    value={formik.values.coupon}
                    editing={editing}
                  >
                    <AddCoupon
                      couponType="purchase"
                      text={t('applyCoupon')}
                      user={user}
                      modelId={item.itemId}
                      modelType={item.itemType}
                      selectedCoupon={formik.values.coupon}
                      onApplyCoupon={(coupon) => {
                        formik.setFieldValue('coupon', coupon);
                      }}
                      onRemoveCoupon={() => {
                        formik.setFieldValue('coupon', null);
                      }}
                      openCouponModal={() => {
                        setCouponType('purchase');
                        setShowCreateCouponForm(true);
                      }}
                    />
                  </Field>
                )}
              </>
            )}
            {item.itemType === 'Fbm::Package' && (
              <>
                <Field
                  label={t('packageActivationDate')}
                  value={formik.values.activationDate || new Date()}
                  editing={editing}
                >
                  <FormDatePicker
                    label={t('packageActivationDate')}
                    name="activationDate"
                    helperText={t('packageActivationDateHelper')}
                  />
                </Field>
                <Field
                  label={t('packageExpirationDate')}
                  value={formik.values.expiresOn}
                  editing={editing}
                >
                  <FormDatePicker
                    label={t('packageExpirationDate')}
                    name="expiresOn"
                    helperText={t('packageExpirationDateHelper')}
                  />
                </Field>
              </>
            )}
            {trainers && (
              <>
                <Field
                  label={t('chargedBy')}
                  value={
                    formik.values.chargedById
                      ? trainers?.find(
                          (t) => t.trainerId === formik.values.chargedById
                        )?.name
                      : null
                  }
                  editing={editing}
                >
                  <FormTrainerSelect
                    name="chargedById"
                    label={t('chargedBy')}
                  />
                </Field>
                <Field
                  label={t('primaryCommissionees')}
                  value={formik.values.primaryCommissioneeIds?.map((id) => {
                    return (
                      trainers?.find((t) => t.trainerId === id)?.name ||
                      t('unknown')
                    );
                  })}
                  editing={editing}
                >
                  <FormTrainerSelect
                    name="primaryCommissioneeIds"
                    label={t('primaryCommissionees')}
                    multiple
                  />
                </Field>
                <Field
                  label={t('secondaryCommissionees')}
                  value={formik.values.secondaryCommissioneeIds?.map((id) => {
                    return (
                      trainers?.find((t) => t.trainerId === id)?.name ||
                      t('unknown')
                    );
                  })}
                  editing={editing}
                >
                  <FormTrainerSelect
                    name="secondaryCommissioneeIds"
                    label={t('secondaryCommissionees')}
                    multiple
                  />
                </Field>
              </>
            )}
            {item.itemType === 'Fbm::Package' && user && (
              <>
                <Field
                  label={t('sharePackageWith')}
                  value={formik.values.users as ClientSearchResult[]}
                  editing={editing}
                >
                  <ClientAutocomplete
                    multiple
                    label={t('selectAccountsToSharePackageWith')}
                    value={formik.values.users as ClientSearchResult[]}
                    onChange={(newClients) => {
                      if (Array.isArray(newClients)) {
                        formik.setFieldValue(
                          'users',
                          newClients.map((client) => ({
                            id: client.id,
                            name: client.name,
                          }))
                        );
                      }
                    }}
                  />
                </Field>
                <Field
                  label={t('shareWithAllDependents')}
                  value={formik.values.shareWithAllDependents}
                  editing={editing}
                >
                  <FormCheckbox
                    name="shareWithAllDependents"
                    label={t('shareWithAllDependentsOf', {
                      name: getUserDisplayName(user),
                    })}
                    useSwitch
                  />
                </Field>
              </>
            )}
            {!!editing && (
              <Button
                sx={{ mt: 3 }}
                variant="prominent"
                type="submit"
                onClick={() => formik.handleSubmit()}
              >
                {t('apply')}
              </Button>
            )}
          </Stack>
        </form>
      </FormikProvider>
      {showCreateCouponForm && (
        <CouponForm type={couponType} afterSave={handleCreateCoupon}>
          {({ submitForm, isValid, isSubmitting }) => (
            <Dialog
              open={showCreateCouponForm}
              title={t('createCoupon')}
              onClose={() => setShowCreateCouponForm(false)}
              actions={[
                {
                  label: t('cancel'),
                  onClick: () => setShowCreateCouponForm(false),
                },
                {
                  label: isSubmitting ? t('creating') : t('createCoupon'),
                  onClick: submitForm,
                  disabled: !isValid || isSubmitting,
                  variant: 'prominent',
                  color: 'primary',
                  startIcon: isSubmitting ? (
                    <CircularProgress size="small" />
                  ) : undefined,
                },
              ]}
            >
              <CouponFormFields />
            </Dialog>
          )}
        </CouponForm>
      )}
    </>
  );
};

export default ItemSettings;
